import React, { useState, useContext } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useAlert } from "../components/AlertContext";
import { PlatformContext } from "../components/Platform/PlatformContext";
import InputField from "../components/Form/InputField";
import Button from "../components/Form/Button";
import OrDivider from "../components/OrDivider";
import PhoneInput from "react-phone-input-2";
import { parsePhoneNumberFromString } from "libphonenumber-js";

const server_url = process.env.REACT_APP_BACKEND_URL;

const SignUp: React.FC = () => {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [surname, setSurname] = useState("");
  const [firstName, setFirstName] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [signedUp, setSignedUp] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();
  const platform = useContext(PlatformContext);
  const { showAlert } = useAlert();
  const isMobile = !platform?.isDesktop;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!agreedToTerms) {
      showAlert(
        "warning",
        "Bitte akzeptiere die Datenschutzrichtlinie und Nutzungsbedingungen."
      );
      return;
    }
    const phoneNumber = parsePhoneNumberFromString(phone);

    if (!phoneNumber || !phoneNumber.isValid()) {
      showAlert("warning", "Please insert a valid phone number.");
      return;
    }
    const e164 = phoneNumber.format("E.164");

    if (password.length < 8) {
      showAlert("warning", "Password should be at least 8 characters!");
      return;
    }

    setPassword("");
    setSubmitted(true);

    try {
      const name = `${firstName} ${surname}`;
      const response = await axios.post(`${server_url}/signup`, {
        email,
        password,
        name,
        phone: e164,
      });
      if (response.data.status) {
        // console.log("Signup successful:", response.data.data);
        setSubmitted(false);
        setSignedUp(true);
      } else {
        if (response.data.error === "user_exists") {
          showAlert(
            "warning",
            "It seems like you already have an Account. Please try loggin in!"
          );
          navigate("/login");
        } else {
          showAlert(
            "error",
            "There was an error when creating your account. Please try again later!"
          );
          // console.log(response.data.error);
          setPassword("");
        }
      }
    } catch (error: any) {
      showAlert(
        "error",
        "Fehler beim Erstellen deines Kontos. Bitte versuche es erneut!"
      );
      setPassword("");
      // console.log("Anmeldefehler:", error.response?.data || error.message);
    } finally {
      setSubmitted(false);
    }
  };

  if (signedUp) {
    return (
      <div className="w-full flex items-center justify-center">
        <div className="p-6 mt-32 bg-white rounded-xl shadow-md w-full max-w-sm">
          <h2 className="mb-4 text-2xl font-semibold text-center">
            Sign Up successfull!
          </h2>
          <p>
            Please check your inbox to confirm your mail before loggin in. The
            mail should arrive within two minutes.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center bg-inherit mb-auto">
      <div className="max-w-sm flex flex-col items-center h-full bg-inherit">
        <form
          onSubmit={handleSubmit}
          className={
            (isMobile ? "px-10 " : "") +
            "pt-6 rounded shadow-md w-full max-w-sm text-white"
          }
        >
          <h2 className="mb-4 text-2xl font-semibold text-center">Sign Up</h2>
          <div className="flex max-w-sm space-x-1">
            <InputField
              type="name"
              value={firstName}
              className="mb-3 "
              placeholder="Name"
              onChange={(e) => setFirstName(e.target.value)}
              required={true}
              isMobile={isMobile}
            />
            <InputField
              type="surname"
              value={surname}
              className="mb-3 "
              placeholder="Surname"
              onChange={(e) => setSurname(e.target.value)}
              required={true}
              isMobile={isMobile}
            />
          </div>
          <PhoneInput
            country="de"
            value={phone}
            onChange={(val) => setPhone("+" + val)}
            specialLabel=""
            containerClass="mb-3 flex items-center border border-gray-500 rounded-sm overflow-hidden"
            inputClass="bg-blue-950 px-3 py-2 text-gray-300 focus:outline-none flex-grow"
            buttonClass="bg-blue-950 border-none focus:outline-none"
            dropdownClass="bg-blue-950 text-gray-300 border border-gray-500"
            placeholder="Phone"
            inputProps={{ required: true }}
          />
          <InputField
            type="email"
            value={email}
            className="mb-3"
            placeholder="E-Mail"
            onChange={(e) => setEmail(e.target.value)}
            required={true}
            isMobile={isMobile}
          />
          <InputField
            type="password"
            value={password}
            placeholder="Password"
            className="mb-10"
            onChange={(e) => setPassword(e.target.value)}
            required={true}
            isMobile={isMobile}
          />
          {/* Checkbox für Nutzungsbedingungen */}
          <div className="mb-4">
            <label className="flex items-center text-sm">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="mr-2"
              />
              <span>
                Ich habe die{" "}
                <Link to="/data" className="text-blue-300 underline">
                  Datenschutzrichtlinie
                </Link>
                ,{" "}
                <Link to="/impressum" className="text-blue-300 underline">
                  Impressum
                </Link>
                , und{" "}
                <Link to="/terms" className="text-blue-300 underline">
                  Nutzungsbedingungen
                </Link>{" "}
                gelesen und akzeptiere sie.
              </span>
            </label>
          </div>
          <Button
            type="submit"
            text="Sign Up"
            isMobile={isMobile}
            disabled={!agreedToTerms || submitted}
          />
        </form>
        <OrDivider isMobile={isMobile} />
        <div className={(isMobile ? "px-10 " : "") + "w-full"}>
          <Button
            type="button"
            text="Log In"
            isMobile={isMobile}
            onClick={() => navigate("/login")}
          />
        </div>
      </div>
    </div>
  );
};

export default SignUp;
